import { forwardRef } from "react";
import { Flex } from "rebass/styled-components";
import { useRecoilValue } from "recoil";

import EmbeddablePlayer from "MVVM/Views/EmbeddablePlayer";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { formatNumber } from "MVVM/Utilities/Clip";
import { formatRelativeTime } from "MVVM/Utilities/Time";
import { ActivePlaylistClipState } from "MVVM/State/embeddablePlaylist";
import allstarWordmark from "assets/images/brand/Wordmark-Light.svg";
import leagueMark from "MVVM/Assets/GameLogos/league.svg";
import uggMark from "MVVM/Assets/Platforms/ugg-mark.svg";

import { Thumbnails } from "../Thumbnails";
import { getMetadataValue } from "../../Utilities";
import { UGGHighlightsTabViewModel } from "./ViewModel";
import * as S from "./styledComponents";

const ALLSTAR_URL = "https://allstar.gg";

const ArrowOutIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 17 17 7" />
    <path d="M8 7h9v9" />
  </svg>
);

const ClapperIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z" />
    <path d="M6.2 5.3l3.1 3.9M12.4 3.4l3.1 4" />
    <path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </svg>
);

const AlertIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
);

const LoadingState = () => (
  <S.PlayerFrame>
    <LoadingSkeleton width="100%" height="100%" />
  </S.PlayerFrame>
);

const UnsupportedState = () => (
  <S.UnsupportedPanel>
    <AlertIcon />
    <S.UnsupportedHeading>
      Sorry, highlights aren't available for this match
    </S.UnsupportedHeading>
    <S.UnsupportedBody>
      Replay data is missing or too old to process. Try another game from your
      match history.
    </S.UnsupportedBody>
  </S.UnsupportedPanel>
);

const GeneratingState = ({ riotID }: { riotID: string | null }) => (
  <S.Panel>
    <S.PanelScrim />
    <S.PanelWash $tint="envy" />
    <S.CornerWordmark src={allstarWordmark} alt="Allstar" />
    <S.PanelBody $center>
      <S.GameBadge>
        <S.GameBadgeIcon src={leagueMark} alt="" />
      </S.GameBadge>
      <Flex flexDirection="column" style={{ gap: 7 }}>
        <S.PanelHeading>
          {riotID
            ? `Rendering ${riotID.split("#")[0]}'s highlight`
            : "Rendering your highlight"}
        </S.PanelHeading>
        <S.PanelSubheading>
          Hang tight, we'll play it here as soon as it's ready.
        </S.PanelSubheading>
      </Flex>
      <S.IndeterminateTrack>
        <S.IndeterminateFill />
      </S.IndeterminateTrack>
    </S.PanelBody>
  </S.Panel>
);

const SignedOutState = ({ loginUrl }: { loginUrl: string | null }) => (
  <S.Panel style={{ justifyContent: "flex-start" }}>
    <S.PanelScrim />
    <S.PanelWash $tint="ugg" />
    <S.CornerWordmark src={allstarWordmark} alt="Allstar" />
    <S.PanelBody>
      <Flex flexDirection="column" style={{ gap: 7 }}>
        <Flex alignItems="center" style={{ gap: 11 }}>
          <S.UggMark src={uggMark} alt="U.GG" />
          <S.SignedOutHeading>
            Log in to U.GG to clip this match
          </S.SignedOutHeading>
        </Flex>
        <S.PanelSubheading>
          Clips are tied to the account that played the game.
        </S.PanelSubheading>
      </Flex>
      {/* target=_top: their login page would be broken inside our frame. Without a
          loginUrl the button stays inert and U.GG owns the action. */}
      <S.PrimaryCta
        as={loginUrl ? "a" : "button"}
        type={loginUrl ? undefined : "button"}
        href={loginUrl || undefined}
        target={loginUrl ? "_top" : undefined}
        rel={loginUrl ? "noopener noreferrer" : undefined}
        $ugg
      >
        Log in to U.GG
      </S.PrimaryCta>
    </S.PanelBody>
  </S.Panel>
);

const CreateState = ({
  onCreate,
  submitting,
  riotID,
}: {
  onCreate: () => void;
  submitting: boolean;
  riotID: string | null;
}) => (
  <S.Panel>
    <S.PanelScrim />
    <S.PanelWash $tint="envy" />
    <S.CornerWordmark src={allstarWordmark} alt="Allstar" />
    <S.PanelBody $center>
      <S.GameBadge>
        <S.GameBadgeIcon src={leagueMark} alt="" />
      </S.GameBadge>
      <Flex flexDirection="column" style={{ gap: 7 }}>
        <S.PanelHeading>
          {riotID
            ? `Clip ${riotID.split("#")[0]}'s best play`
            : "Clip the best play from this match"}
        </S.PanelHeading>
        <S.PanelSubheading>
          Allstar finds the standout moment from this match and renders it in
          1080p60. It will play right here when it is done.
        </S.PanelSubheading>
      </Flex>
      <S.PrimaryCta
        as="button"
        type="button"
        onClick={onCreate}
        disabled={submitting}
      >
        <ClapperIcon />
        {submitting ? "Sending..." : "Clip this match"}
      </S.PrimaryCta>
    </S.PanelBody>
  </S.Panel>
);

const ExpiredState = () => (
  <S.Panel>
    <S.PanelScrim />
    <S.PanelWash $tint="ugg" />
    <S.CornerWordmark src={allstarWordmark} alt="Allstar" />
    <S.PanelBody $center>
      <S.GameBadge>
        <RefreshIcon />
      </S.GameBadge>
      <Flex flexDirection="column" style={{ gap: 7 }}>
        <S.PanelHeading>Refresh to clip this match</S.PanelHeading>
        <S.PanelSubheading>
          This tab has been open a while, so it needs a fresh start. Reload the
          page and your highlight is one click away.
        </S.PanelSubheading>
      </Flex>
    </S.PanelBody>
  </S.Panel>
);

const NoneState = () => (
  <S.Panel>
    <S.PanelScrim />
    <S.PanelWash $tint="envy" />
    <S.CornerWordmark src={allstarWordmark} alt="Allstar" />
    <S.PanelBody $center>
      <Flex flexDirection="column" style={{ gap: 7 }}>
        <S.PanelHeading>No highlights from this match</S.PanelHeading>
        <S.PanelSubheading>
          There was no standout play to clip here. Check back after your next
          game.
        </S.PanelSubheading>
      </Flex>
    </S.PanelBody>
  </S.Panel>
);

const CappedState = () => (
  <S.Panel>
    <S.PanelScrim />
    <S.PanelWash $tint="ugg" />
    <S.CornerWordmark src={allstarWordmark} alt="Allstar" />
    <S.PanelBody $center>
      <Flex flexDirection="column" style={{ gap: 7 }}>
        <S.PanelHeading>You've used all your clips this month</S.PanelHeading>
        <S.PanelSubheading>
          Free U.GG accounts get a limited number of clips each month. Upgrade
          your U.GG membership to keep clipping.
        </S.PanelSubheading>
      </Flex>
      {/* U.GG owns the upsell: their membership, their checkout. */}
      <S.PrimaryCta as="button" type="button" $ugg>
        Upgrade on U.GG
      </S.PrimaryCta>
    </S.PanelBody>
  </S.Panel>
);

const RateLimitedState = () => (
  <S.Panel>
    <S.PanelScrim />
    <S.PanelWash $tint="caution" />
    <S.CornerWordmark src={allstarWordmark} alt="Allstar" />
    <S.PanelBody $center>
      <Flex flexDirection="column" style={{ gap: 7 }}>
        <S.PanelHeading>Slow down a moment</S.PanelHeading>
        <S.PanelSubheading>
          That's a few too many requests in a row. Wait a minute and try again.
        </S.PanelSubheading>
      </Flex>
    </S.PanelBody>
  </S.Panel>
);

const ReadyState = ({
  clips,
  platform,
}: {
  clips: ReturnType<typeof UGGHighlightsTabViewModel>["clips"];
  platform: string;
}) => {
  const [clip] = clips;
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const profileIcon = getMetadataValue(clip?.metadata, "Profile Icon") || "1";
  // clip.views is real, but a clip made seconds ago always reads "0 views".
  const views = clip?.views ?? 0;
  const byline = [
    clip?.username,
    views > 0 ? `${formatNumber(views)} views` : "",
    clip?.createdDate ? formatRelativeTime(clip.createdDate) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <S.ReadyStack>
      {/* The player reads its clip from Recoil; don't mount before one is set. */}
      <S.PlayerFrame>
        {activePlaylistClip ? (
          <EmbeddablePlayer
            className="player-component"
            userRole="viewer"
            onPlayEndVariant={clips.length > 1 ? "nextUpPlaylist" : undefined}
            /* Same as NextUpPlayer and LeagueEmbeddablePlaylist: the mid-roll banner
               promises a next video, and with one clip it just replays this one. */
            hideRampBottomCenter
          />
        ) : (
          <LoadingSkeleton width="100%" height="100%" />
        )}
      </S.PlayerFrame>
      <S.ClipMetaRow>
        <S.ClipAvatar>
          <S.ClipAvatarImage
            src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/lol/summoner_icons/${profileIcon}.png`}
            alt=""
          />
        </S.ClipAvatar>
        <Flex flexDirection="column" style={{ gap: 1, minWidth: 0 }}>
          <S.ClipTitle>{clip?.title || "Your highlight"}</S.ClipTitle>
          <S.ClipByline>{byline}</S.ClipByline>
        </Flex>
      </S.ClipMetaRow>
      {clips.length > 1 && (
        <S.ClipRail>
          <Thumbnails
            clipsToDisplay={clips}
            renderLolPlaylist
            platform={platform}
          />
        </S.ClipRail>
      )}
    </S.ReadyStack>
  );
};

export const UGGHighlightsTab = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    handleMouseEnter: () => void;
  }
>(({ platform, handleMouseEnter }, ref) => {
  const { state, context, clips, submitting, createHighlights } =
    UGGHighlightsTabViewModel({
      platform,
    });

  const renderState = () => {
    switch (state) {
      case "loading":
        return <LoadingState />;
      case "ready":
      case "multi":
        return <ReadyState clips={clips} platform={platform} />;
      case "none":
        return <NoneState />;
      case "create":
        return (
          <CreateState
            onCreate={createHighlights}
            submitting={submitting}
            riotID={context.riotID}
          />
        );
      case "expired":
        return <ExpiredState />;
      case "signedout":
        return <SignedOutState loginUrl={context.loginUrl} />;
      case "capped":
        return <CappedState />;
      case "ratelimited":
        return <RateLimitedState />;
      case "generating":
        return <GeneratingState riotID={context.riotID} />;
      case "unsupported":
      default:
        return <UnsupportedState />;
    }
  };

  return (
    <S.Shell ref={ref} onMouseEnter={handleMouseEnter}>
      <S.OpenInAllstarRow>
        <S.OpenInAllstarLink
          href={ALLSTAR_URL}
          target="_blank"
          rel="noreferrer"
        >
          Open in Allstar
          <ArrowOutIcon />
        </S.OpenInAllstarLink>
      </S.OpenInAllstarRow>
      {renderState()}
    </S.Shell>
  );
});
